import { rejectCodes } from '@ourbranch/lookups';

import { defineInternalTask } from '../../../config/types';
import { internalTasks, reviewTeams } from '../../../config/constants';

import { baseBindState } from './lib/helpers';

/**
 * Numeric reject codes (from `@ourbranch/lookups`) mapped to human-readable labels.
 * The runtime field `offer.quote.global.propertyConcern` is a single numeric code —
 * see `dynamo-be/lambdas/bind-requests-to-accounts/src/index.js:3365-3395` for the
 * canonical list the legacy ticket creation matches against.
 */
const PROPERTY_CONCERN_LABELS: Record<number, string> = {
  [rejectCodes.NO_COMMERCIAL_PROPERTIES]: 'Commercial Property',
  [rejectCodes.PROPERTY_MISMATCH]: 'Property Type Mismatch',
  [rejectCodes.UNSUPPORTED_PROPERTY]: 'Unsupported Property',
  [rejectCodes.VACANT_HOME]: 'Vacant Home',
  [rejectCodes.UNSUPPORTED_ROOF_MATERIAL]: 'Unsupported Roof Material',
  [rejectCodes.HISTORIC_HOME]: 'Historic Home',
  [rejectCodes.FL_FLAT_ROOFS]: 'Flat Roof',
  [rejectCodes.VERISK_360_WOOD]: 'Wood Roof',
  [rejectCodes.HOME_CHARACTERISTIC_ASBESTOS]: 'Asbestos Home Materials'
};

export const homeRequiresManualReview = defineInternalTask({
  taskName: internalTasks.HomeRequiresManualReview,
  config: {
    staffDisplayName: 'Home characteristics eligibility: review needed',
    reviewTeam: reviewTeams.uwOps,
    policyTypes: ['H'],
    enabled: true,
    trigger: ({ policy, offer }) => {
      // Runtime shape: `offer.quote.global.propertyConcern` is a single Int rejection
      // code (or null) — the legacy bind-ticket creator at
      // `bind-requests-to-accounts/src/index.js:3365-3395` uses it this way. Coerce to
      // an array of one so downstream stays array-shaped.
      const propertyConcern = offer?.quote?.global?.propertyConcern;
      if (typeof propertyConcern !== 'number' || !PROPERTY_CONCERN_LABELS[propertyConcern]) {
        return null;
      }
      return {
        data: { propertyConcerns: [propertyConcern] },
        bindState: baseBindState(policy)
      };
    },
    description: ({ data }) => {
      const concerns = (data.propertyConcerns || [])
        .map((code) => `- ${PROPERTY_CONCERN_LABELS[code] || 'Unsupported Property'}`)
        .join('\n');
      return `This home was flagged for the following concern(s) and requires manual verification that these are incorrect:
${concerns}`;
    }
  }
});
